/* eslint-disable react/display-name */
import {
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';

import {
  IconButton as IconButtonExternal,
  IconButtonProps as IconButtonPropsExternal,
} from '@/components/form/baseInputs/Button/IconButton';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';

import { CellContainer } from '../../components/cells';
import { CellRenderProp } from '../../types';
import { getCellRenderProp } from '../../utils/getRenderProp';

interface IconButtonRendererProps<
  R extends GridValidRowModel,
  V,
  F,
  N extends GridTreeNodeWithRender,
> {
  iconButtonProps?: CellRenderProp<R, V, F, N, IconButtonProps<R>>;
}

type RelevantIconButtonProps = Omit<IconButtonPropsExternal, 'onClick'>;

interface IconButtonProps<R extends GridValidRowModel>
  extends RelevantIconButtonProps {
  handleClick: (row: R) => void;
}

function IconButton<R extends GridValidRowModel = GridValidRowModel>({
  props,
  row,
}: {
  props: IconButtonProps<R>;
  row: R;
}) {
  const { handleClick, ...otherProps } = props ?? {};
  return (
    <IconButtonExternal onClick={() => handleClick?.(row)} {...otherProps} />
  );
}

export const IconButtonRenderer = <
  R extends GridValidRowModel = GridValidRowModel,
  V extends string = string,
  F = V,
  N extends GridTreeNodeWithRender = GridTreeNodeWithRender,
>(
  factoryProps: IconButtonRendererProps<R, V, F, N>
) => {
  return (props: GridRenderCellParams<R, V, F, N>) => {
    const iconButtonProps = getCellRenderProp({
      props,
      prop: factoryProps?.iconButtonProps,
      defaultValue: {
        handleClick: () => {
          // TODO
        },
        icon: ChevronRightIcon,
        size: 'xs',
        ariaLabel: 'Icon Button Cell',
      },
    });

    return (
      <CellContainer align={props.colDef.align}>
        <IconButton props={iconButtonProps} row={props.row} />
      </CellContainer>
    );
  };
};
